// Control plane REST API client — talks to /api/control/* on port 8004 (via nginx)

import type { CommandRecord, ControlConfigState, ControlCommand } from '../types/control';

const BASE = import.meta.env.VITE_CONTROL_API_BASE || '';

function _headers(token: string | null) {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function _handleResp<T>(res: Response): Promise<T> {
  if (res.status === 428) {
    // Destructive command needs confirm_token — throw so UI can prompt
    const body = await res.json();
    const err: any = new Error('requires_confirm');
    err.confirmData = body.detail;
    throw err;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ---------- Config ----------

export async function fetchControlConfig(token: string | null): Promise<ControlConfigState> {
  const res = await fetch(`${BASE}/api/control/config`, { headers: _headers(token) });
  const data = await _handleResp<{ ok: boolean; config: any }>(res);
  // Merge flat global keys + per_symbol keys into a ControlConfigState shape
  const { per_symbol, ...globals } = data.config;
  const spread_bps_bid: Record<string, number> = {};
  const spread_bps_ask: Record<string, number> = {};
  const inventory_target: Record<string, number> = {};
  if (per_symbol) {
    for (const [sym, fields] of Object.entries(per_symbol as Record<string, Record<string, number>>)) {
      if ('spread_bps_bid' in fields) spread_bps_bid[sym] = fields.spread_bps_bid;
      if ('spread_bps_ask' in fields) spread_bps_ask[sym] = fields.spread_bps_ask;
      if ('inventory_target' in fields) inventory_target[sym] = fields.inventory_target;
    }
  }
  return {
    market_making_enabled: Boolean(globals.market_making_enabled ?? false),
    market_making_mode: (globals.market_making_mode as any) ?? 'manual',
    ml_enabled: Boolean(globals.ml_enabled ?? false),
    ml_model_type: globals.ml_model_type ?? 'heuristic',
    initial_capital: Number(globals.initial_capital ?? 500_000_000),
    settlement_mode: globals.settlement_mode ?? 'instant',
    quoting_paused: Boolean(globals.quoting_paused ?? false),
    spread_bps_bid,
    spread_bps_ask,
    inventory_target,
    default_time_in_force: Number(globals.default_time_in_force ?? 1),
    default_expire_date: globals.default_expire_date ?? '',
    default_expire_time: globals.default_expire_time ?? '',
  };
}

export async function postControlConfig(
  token: string | null,
  fields: Record<string, unknown>,
  reason?: string,
): Promise<{ ok: boolean; written: string[]; errors: { field: string; error: string }[] }> {
  const res = await fetch(`${BASE}/api/control/config`, {
    method: 'POST',
    headers: _headers(token),
    body: JSON.stringify({ fields, reason, source: 'ui' }),
  });
  return _handleResp(res);
}

// ---------- Commands ----------

export async function fetchCommands(token: string | null): Promise<CommandRecord[]> {
  const res = await fetch(`${BASE}/api/control/commands`, { headers: _headers(token) });
  const data = await _handleResp<{ ok: boolean; commands: CommandRecord[] }>(res);
  return data.commands;
}

export interface IssueCommandResult {
  ok: boolean;
  id: string;
  command: string;
  status: string;
}

export interface ConfirmRequired {
  requires_confirm: true;
  confirm_token: string;
  expires_in_seconds: number;
  preview: string;
}

export async function issueCommand(
  token: string | null,
  command: ControlCommand,
  args?: Record<string, unknown>,
  confirmToken?: string,
): Promise<IssueCommandResult> {
  const res = await fetch(`${BASE}/api/control/commands`, {
    method: 'POST',
    headers: _headers(token),
    body: JSON.stringify({ command, args: args ?? {}, confirm_token: confirmToken, source: 'ui' }),
  });
  return _handleResp<IssueCommandResult>(res);
}

export async function previewCommand(
  token: string | null,
  command: ControlCommand,
  args?: Record<string, unknown>,
): Promise<{ preview: { description?: string; safe?: boolean }; requires_confirm: boolean; confirm_token?: string }> {
  const res = await fetch(`${BASE}/api/control/commands/preview`, {
    method: 'POST',
    headers: _headers(token),
    body: JSON.stringify({ command, args: args ?? {}, source: 'ui' }),
  });
  return _handleResp(res);
}
